// L14 — **THE SEVEN-DAY GRACE, AND THE CLOCK THIS HOUSE OWNS RATHER THAN THE ONE STRIPE HAPPENS TO
// RUN.** Her order: full access for seven days after a card fails, warm reminders on day 1 and day
// 5, the tap opens card update, and RECOVERY ENDS GRACE SILENTLY.
//
// **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180): HER CLAUSE 1 WAS HALF TRUE BY ACCIDENT.**
// `ENTITLING_STATUSES` in `server/stripe-webhook.js` already carries `past_due` and its own comment
// says why — *"a card that failed once is a payment problem, not a decision to leave"* — so a failed
// renewal already keeps every door open today. **What did not exist is a clock.** `subscriptions`
// carried no failure column at all, `recordPaymentFailure` wrote one `gate_log` row and a runtime
// console line, and *how many members are in grace right now* had NO ANSWER — which is the same
// absence D-4 and MONEY-1 each had to repair before a cause could be named. Access was therefore
// governed by Stripe's dunning window, which this house does not set, cannot count from and cannot
// remind against.
//
// **IT IS A SHARED `.js` LAW FOR `strings.js`' OWN REASON: THE SERVER COMPOSES MEMBER-FACING COPY
// TOO.** The reminder's sentence is written by a scheduled job on the server and `src/lib/*.ts` is
// CLIENT-ONLY; the query parameter her tap lands on is read by App.tsx. A roster written on one side
// is copied to the other within a round, and a second answer to *how long is grace* is a second
// answer to *when does she lose the house* (R28/R29).
//
// **IT IMPORTS NOTHING**, which is the entry requirement every law in `src/` is held to, and is what
// lets `scripts/l14-payment-grace.mjs` load and RUN every clause below with no database, no network
// and no clock of its own.

/** One day, in milliseconds. The window is measured in ELAPSED days from the stamp, never in
 * calendar days in some zone — see `graceDaysElapsed`. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/** HER NUMBER. Seven days of FULL access from the first failed charge. L15's freeze is what happens
 * on the far side of it; this module owns the window and says nothing about what follows. */
// >>> **P3, HER RULING (2026-10-04): "I'm only giving a three-day grace period until their subscription ends, and they have
// to subscribe."** <<< Three days of full access and reminders while Stripe retries; still unpaid, the subscription ENDS
// (canceled, not paused) and she subscribes again to return. Her seven became three here, at the declaration.
export const GRACE_DAYS = 3;

/** HER TWO REMINDERS, in her own words: day 1 and day 5. Declared ONCE and read by the law, the job
 * and the fixture, so a third reminder is a one-line decision in the open rather than a schedule
 * spread across a job and a copy table (R11/R31). */
// P3: day 1 is the moment the payment fails (her friendly notice), then day 2 and day 3. Day N is due once N-1 whole days
// have passed (dueReminderDay), so the three land at 0, 24 and 48 hours and the end comes at 72.
export const GRACE_REMINDER_DAYS = [1, 2, 3];

/**
 * THREE STATES, NEVER TWO (R18), AND THE THIRD IS WHAT L15 IS GOING TO ACT ON.
 *
 *   `none`    — there is no failure standing. She is an ordinary paying member.
 *   `grace`   — a card failed and the seven days have not run out. FULL ACCESS, by her clause.
 *   `expired` — a card failed and the window is spent. **This module does not close a door**; it
 *               names the fact so exactly one place decides what happens next.
 *
 * Collapsing `expired` into `none` is how a member sits in a grace that never ends; collapsing it
 * into `grace` is how nobody is ever reminded that it is running out.
 */
export const GRACE_STATES = { NONE: "none", GRACE: "grace", EXPIRED: "expired" };
export const GRACE_STATE_VALUES = Object.values(GRACE_STATES);

/**
 * **WHEN THE CLOCK STARTED, AND AN UNREADABLE STAMP IS `null` RATHER THAN A GUESS.**
 *
 * The value arrives from Postgres as a `Date`, from a JSON payload as an ISO string, and from a
 * fabricated row as whatever somebody typed. `Number(true)` is 1 and `Number("")` is 0 — the exact
 * coercion that once cast a whole chart at the equator (R136/R137) — and **epoch 0 is a stamp from
 * 1970, which is seven days expired**, so a coerced value here does not fail loudly, it FREEZES
 * somebody. Only a real date is a date.
 */
export function graceStartedAt(row = {}) {
  const raw = row?.payment_failed_at ?? row?.paymentFailedAt ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? raw.getTime() : null;
  if (typeof raw === "number") return Number.isFinite(raw) && raw > 0 ? raw : null;
  if (typeof raw !== "string") return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * **ELAPSED WHOLE DAYS FROM THE STAMP, DELIBERATELY NOT CALENDAR DAYS IN A ZONE.**
 *
 * A calendar reading would make "day 1" arrive at midnight — which for a card that failed at 11pm is
 * an hour later, and for one that failed at 1am is twenty-three hours later. The window she is being
 * promised is SEVEN DAYS OF ACCESS, so it is measured the way she would measure it: from the moment
 * it happened. It also means this law needs no timezone, which is what keeps it pure.
 */
export function graceDaysElapsed(startedMs, nowMs) {
  if (!Number.isFinite(startedMs) || !Number.isFinite(nowMs)) return 0;
  return Math.max(0, Math.floor((nowMs - startedMs) / DAY_MS));
}

/** Where the window closes. `null` when no clock is running, which is a different fact from a window
 * that has already closed (R18) and is why this is not a number with a sentinel. */
export function graceEndsAtMs(row = {}) {
  const started = graceStartedAt(row);
  return started === null ? null : started + GRACE_DAYS * DAY_MS;
}

/**
 * THE STATE. **An unreadable stamp answers `none`, and that direction is a ruling rather than a
 * default (R19).** With no readable date this house does not know when grace began — so the safe
 * answer is the one that changes nothing: she keeps the access `past_due` already gives her, no
 * reminder claiming a deadline nobody can compute goes out, and nothing downstream freezes her off a
 * value that could not be read. The other direction locks a paying member out over a bad column.
 */
export function graceState(row = {}, nowMs = 0) {
  const started = graceStartedAt(row);
  if (started === null) return GRACE_STATES.NONE;
  return graceDaysElapsed(started, nowMs) >= GRACE_DAYS ? GRACE_STATES.EXPIRED : GRACE_STATES.GRACE;
}

/** How many whole days of grace are left, floored at zero. This is the number her reminder says out
 * loud, so it is derived from the same window the state is — never a second subtraction at a call
 * site (R28/R29). */
export function graceDaysLeft(row = {}, nowMs = 0) {
  const started = graceStartedAt(row);
  if (started === null) return 0;
  return Math.max(0, GRACE_DAYS - graceDaysElapsed(started, nowMs));
}

// ---------------------------------------------------------------------------
// L17 — **WHAT THE DURABLE LOG CALLS THESE TWO EVENTS, DECLARED ONCE.**
// ---------------------------------------------------------------------------

/**
 * **THE FAILURE AND THE RECOVERY ARE THE TWO ROWS HER RECOVERY LINE IS COUNTED FROM, AND A GATE NAME
 * TYPED AT THE WRITER AND AGAIN AT THE READER IS TWO ANSWER KEYS (R28/R29).**
 *
 * The direction that costs something is silent: a reader asking for a name nobody writes counts ZERO
 * forever, and zero is exactly what a quiet month looks like — so her line would read *nobody's card
 * failed* while every card in the house was failing, with nothing red anywhere.
 *
 * `stripe-payment-failed` is NOT a new name; it is the one `recordPaymentFailure` has always
 * written, lifted to a declaration so the count and the write cannot drift. `stripe-payment-recovered`
 * is L17's, and it exists because **`endPaymentGrace` sets `payment_failed_at = NULL`, which destroys
 * the only evidence that a recovery ever happened** — so *how many recovered this period* could not
 * be answered from the row by anybody. The same absence D-4, MONEY-1 and CE-4 each had to repair
 * before a count could exist.
 *
 * **A ROW, NOT A COLUMN, AND THAT IS THE DESIGN.** A `recovered_at` column answers *the last time she
 * recovered* and is overwritten by the next one, so a per-period COUNT still has no source; and a
 * member who fails and recovers three times in a quarter is three facts, not one. `gate_log` already
 * holds the FAILURE side, so the symmetrical answer is one fire-and-forget row on the recovery and no
 * migration at all (D-1/D-4's own ruling: reuse the durable log rather than mint a table for a fact
 * it already holds).
 */
export const GRACE_LOG_FAILED = "stripe-payment-failed";
export const GRACE_LOG_RECOVERED = "stripe-payment-recovered";

/**
 * L15 — **THE FREEZE, AND IT IS DERIVED RATHER THAN WRITTEN.**
 *
 * Her clause: *"Day 8: FREEZE, NOT DELETE. Access gates to a warm reactivation screen; one tap to pay
 * reopens everything instantly. DATA PRESERVED INDEFINITELY, freeze forever, deletion ONLY on the
 * member's own request."*
 *
 * **THERE IS NO `frozen` COLUMN AND NO JOB THAT WRITES ONE, WHICH IS THE WHOLE DESIGN.** A freeze
 * written as a column needs somebody to write it, and a job that did not run leaves a member
 * unfrozen past her window or — far worse — frozen after she has already paid. Derived from the
 * stamp L14 already keeps, it is true the instant the clock passes and FALSE the instant the webhook
 * clears the stamp, with nothing in between to go stale. That is also what makes her *"one tap
 * reopens everything instantly"* structural: the recovery event clears `payment_failed_at` and
 * `invalidateEntitlements` runs on the same webhook, so the door is open before she is back on the
 * page.
 *
 * **IT IS EXACTLY `expired`, NEVER A SECOND THRESHOLD.** Day 8 is the far side of her seven days,
 * so a separate freeze constant would be a second answer to one boundary (R28/R29) — and the one
 * that drifted would either lock somebody out early or leave the door open forever.
 *
 * **NOTHING HERE DELETES ANYTHING.** A freeze is a STATE this function answers, and every row of
 * hers is untouched by it.
 */
export function accessFrozen(row = {}, nowMs = 0) {
  return graceState(row, nowMs) === GRACE_STATES.EXPIRED;
}

/** The day the freeze begins, for a sentence that wants to name it. Derived, so it moves with her
 *  window rather than being typed a second time. */
export const FREEZE_DAY = GRACE_DAYS + 1;

/** Which reminder has already gone out. A row that has had none reads 0; anything unreadable reads 0
 * too, because *we cannot tell* and *none have been sent* both mean the next send has to be decided
 * by the CLAIM in the store's WHERE clause rather than by this number (R18: the honest answer here
 * is the one that defers to the thing that cannot race). */
export function graceRemindedDay(row = {}) {
  const raw = row?.grace_reminded_day ?? row?.graceRemindedDay ?? 0;
  if (typeof raw !== "number" && typeof raw !== "string") return 0;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : 0;
}

/**
 * **WHICH REMINDER IS DUE RIGHT NOW, AND IT IS THE HIGHEST ONE RATHER THAN THE NEXT ONE.**
 *
 * If the job has been down for six days, sending day 1 and then day 5 as two messages is two
 * notifications about one failure — and the day-1 wording ("you have a week") is FALSE by the time it
 * arrives. The latest applicable reminder is sent, the earlier ones are stepped over, and the stamp
 * records which one really went.
 *
 * **NOTHING IS DUE ONCE THE WINDOW IS SPENT.** A reminder that says a week of access is waiting,
 * arriving on day nine, is a promise this house cannot keep (R18: `expired` is its own fact and L15
 * owns what is said about it).
 *
 * @returns {number|null} the reminder day to send, or `null` when there is nothing to send.
 */
export function dueReminderDay(row = {}, nowMs = 0) {
  if (graceState(row, nowMs) !== GRACE_STATES.GRACE) return null;
  const elapsed = graceDaysElapsed(graceStartedAt(row), nowMs);
  const already = graceRemindedDay(row);
  const due = GRACE_REMINDER_DAYS.filter((d) => elapsed + 1 >= d && d > already); // P3: day 1 is the day it failed
  return due.length ? Math.max(...due) : null;
}

// ---------------------------------------------------------------------------
// WHAT SHE IS TOLD. P5's three-line law, and S1-A/R117's: never the machinery, never a status code,
// never the word `past_due`.
// ---------------------------------------------------------------------------

/**
 * **LINE TWO, AND IT IS CANNED AND SHORT BECAUSE THAT FIELD IS THE ONE iOS TRUNCATES.**
 *
 * P2-9 MEASURED it: a title runs to about one bold line (`PUSH_TITLE_MAX` is 56), and her 140-
 * character daily line reached a lock screen cut off mid-word. So the day-specific half lives in the
 * BODY and in the email, where there is room, and this field holds one sentence that is true of both
 * reminders. **It carries no number, no member fact and no brand**, so there is nothing in it for
 * P2-4C's leak net or the brand-echo law to catch and nothing that can rot when the window moves.
 */
export const GRACE_PUSH_TITLE = "Your card did not go through";

/**
 * **LINE THREE — THE WARM HALF, AND IT SAYS WHAT HAS *NOT* HAPPENED FIRST.** Her word is *warm*, and
 * the thing a member is actually frightened of when a payment fails is that she has already lost
 * something. Nothing has been taken, and that is the first clause of both sentences.
 *
 * The number is `graceDaysLeft`'s, so the sentence and the window cannot disagree, and the singular
 * is handled because "1 days" is the kind of small wrongness that makes a warm line read as a robot.
 */
export function graceReminderBody(day, daysLeft) {
  const n = Math.max(0, Number(daysLeft) || 0);
  const days = `${n} ${n === 1 ? "day" : "days"}`;
  // P3: her three days, and on the far side the membership ENDS (her word), never "pauses".
  if (Number(day) >= 2) {
    return `You still have everything. There ${n === 1 ? "is" : "are"} ${days} left to update your card before your membership ends.`;
  }
  return `Your payment did not go through. Nothing has changed yet: you have ${days} to update your card, and it is one tap.`;
}

/** The subject line of the same reminder by email, where there is room for the day to show. */
export function graceEmailSubject(day) {
  return Number(day) >= 2 ? "Your membership is waiting on your card" : "Your card did not go through";
}

// E3 (owner, 2026-08-13): the reminder's CALL TO ACTION. It was a bare literal inside the mail's
// HTML; it is declared here beside the rest of this rail's copy, so the shell is handed a label
// rather than composing one and the sentence lives with its siblings.
export const GRACE_EMAIL_CTA = "Update your card";

/**
 * The email's own words. **It is the push's two sentences plus the one thing a lock screen has no
 * room for: what to do.** Composed here rather than in the mailer so the two carriers cannot drift
 * apart — the same reason `houseNotificationText` is one sentence for the bell and the push.
 */
export function graceEmailParagraphs(day, daysLeft) {
  return [
    graceReminderBody(day, daysLeft),
    "Update your card and everything carries on exactly as it is. If it is not updated in time, your membership ends and you can subscribe again whenever you like.",
  ];
}

// ---------------------------------------------------------------------------
// WHERE THE TAP LANDS. Her clause: **the tap opens card update.**
// ---------------------------------------------------------------------------

/**
 * **THE BILLING SCREEN IS STRIPE'S AND THE ONLY WAY TO IT IS A SIGNED-IN POST**
 * (`/api/membership/portal`, which reads the customer off her own row and never takes one from the
 * client). A notification carries a URL, not a POST — so the tap lands on the membership page with
 * this flag on it and the page opens the door she just asked for.
 *
 * **IT IS A REQUEST, NOT A REDIRECT WRITTEN INTO A LINK.** The portal session is minted for the
 * signed-in member at the moment she arrives, so a link that leaks, is shared or is opened a week
 * later opens nothing but the membership page.
 */
export const BILLING_PARAM = "billing";
export const BILLING_UPDATE_URL = `/membership?${BILLING_PARAM}=1`;

/**
 * **DID SHE ASK FOR THE CARD SCREEN?** `joinRequested`'s law one door over, and for its reason
 * (R136/R137): a query value is always a string and `Boolean("false")` and `Boolean("0")` are both
 * `true`, so a bare truthiness test would send a member who explicitly said no straight out to
 * Stripe. The affirmative spellings are accepted, because a hand-written link that works is the
 * recoverable direction and one that silently does nothing is not (R19).
 */
const BILLING_REFUSED = ["", "0", "false", "no", "off"];
export function billingRequested(search) {
  const raw = new URLSearchParams(String(search || "")).get(BILLING_PARAM);
  if (raw === null) return false;
  return !BILLING_REFUSED.includes(String(raw).trim().toLowerCase());
}

/**
 * The address with the flag swept off it. **It is read once and cleaned**, which is
 * `addressWithoutFunnelHandoff`'s own discipline one door up: left in place, a refresh or a back
 * button re-opens Stripe's billing screen under a member who has already finished with it.
 */
export function addressWithoutBillingFlag(search, pathname = "/") {
  const params = new URLSearchParams(String(search || ""));
  if (!params.has(BILLING_PARAM)) return "";
  params.delete(BILLING_PARAM);
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ""}`;
}
